import { reactive } from 'vue'

import { OlympusClient } from '@/OlympusClient'

interface BrandAppState {
  hasApp: boolean
  hasAppStore: boolean
  hasPlayStore: boolean
}

const cache = reactive<Record<string, BrandAppState>>({})

/**
 * Returns whether the brand has at least one live app (iOS or Android).
 * Uses the same logic as Olympus getMobileAppUrls: ios.promote && ios.appStoreId or android.promote && android.bundleId.
 * Returns undefined if not yet loaded for this brand.
 * @param brandId - Brand entity id
 */
export function getBrandHasApp(
  brandId: string | undefined
): boolean | undefined {
  if (!brandId) return undefined
  return cache[brandId]?.hasApp
}

/**
 * Returns per-platform app availability for the brand.
 * Use in the Get the App Buttons preview to show only iOS/Android buttons that the brand actually has.
 * Returns undefined if not yet loaded for this brand.
 * @param brandId - Brand entity id
 */
export function getBrandAppPlatforms(
  brandId: string | undefined
): { hasAppStore: boolean; hasPlayStore: boolean } | undefined {
  if (!brandId) return undefined
  const state = cache[brandId]
  if (!state) return undefined
  return { hasAppStore: state.hasAppStore, hasPlayStore: state.hasPlayStore }
}

/**
 * Fetches the brand's mobile app from Olympus and derives "has app" flags.
 * Uses the existing paginateMobileApps endpoint with brandId filter.
 * Call when the email builder loads a resource so the Add Section modal and Get the App Buttons preview show the correct state.
 * @param brandId - Brand entity id
 */
export async function loadBrandHasApp(brandId: string): Promise<void> {
  try {
    const { results } = await OlympusClient.mobileApps.paginateMobileApps({
      query: { brandId, limit: 1 },
    })
    const app = results[0]
    if (!app) {
      cache[brandId] = {
        hasApp: false,
        hasAppStore: false,
        hasPlayStore: false,
      }
      return
    }
    // Align with Olympus getMobileAppUrls logic: promote flag + ID must both be set
    const hasAppStore = Boolean(app.ios?.promote && app.ios?.appStoreId)
    const hasPlayStore = Boolean(app.android?.promote && app.android?.bundleId)
    cache[brandId] = {
      hasApp: hasAppStore || hasPlayStore,
      hasAppStore,
      hasPlayStore,
    }
  } catch {
    cache[brandId] = { hasApp: false, hasAppStore: false, hasPlayStore: false }
  }
}
